import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useI18n } from './i18n';
import { unsubscribeByToken } from './api';

/**
 * Where a one-click unsubscribe link lands, for a mail client that did not do it itself
 * (F-106).
 *
 * **Public, with no session, and that is the whole point.** Somebody who no longer wants our
 * mail must not have to sign in to stop it — an unsubscribe that asks for a password is how
 * a person reaches for the spam button instead, and a complaint costs the sending domain far
 * more than an opt-out does. The token in the URL is the authorisation: it names one address
 * hash and carries a tag only we can produce.
 *
 * **It POSTs from here rather than being a link that acts on GET.** A URL that unsubscribes
 * when it is fetched is one every spam filter, link-preview fetcher and mail proxy in the
 * chain follows on the recipient's behalf — so the API redirects a GET to this page and only
 * the POST changes anything. A modern client that understands RFC 8058 never gets here at
 * all: it POSTs to the API directly and shows its own confirmation.
 *
 * **It reports success whatever happened, because the API says 200 whatever happened.** A
 * distinguishable failure would let anybody holding a guessed token learn whether an address
 * is one of ours. Claiming more than that would be worse than saying nothing, so the page
 * says what is true either way: the request was recorded. The one thing it does say
 * differently is when there is no token at all — that is a person who navigated here by
 * hand, and sending them to their account settings is the only useful answer.
 */
export default function Unsubscribe() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [stage, setStage] = useState<'working' | 'done' | 'no_link'>(
    token ? 'working' : 'no_link',
  );

  useEffect(() => {
    if (!token) return;
    let live = true;
    void unsubscribeByToken(token).then(() => {
      // Guarded against a component unmounted mid-flight. The request still lands; only
      // the state update is dropped, which is the correct half to abandon.
      if (live) setStage('done');
    });
    return () => {
      live = false;
    };
  }, [token]);

  return (
    <section className="panel">
      <h2>{t('unsub.title')}</h2>
      {stage === 'working' && <p className="muted">{t('unsub.working')}</p>}
      {stage === 'done' && (
        <p role="status">
          {/* Said explicitly that sign-in still works. Somebody stopping one kind of mail
              should not be left wondering whether they have locked themselves out. */}
          {t('unsub.done')}
        </p>
      )}
      {stage === 'no_link' && <p className="muted">{t('unsub.noLink')}</p>}
      <div className="actions">
        <Link className="button" to="/">
          {t('unsub.home')}
        </Link>
      </div>
    </section>
  );
}
